import { z } from 'zod';

/**
 * Schéma de validation du formulaire de tâche (TaskBuilder).
 *
 * Contrairement aux autres schémas, celui-ci ne valide pas la charge utile
 * envoyée : TaskBuilder la calcule (expressions cron, dates formatées) à partir
 * de ce que l'utilisateur a saisi, et c'est cette saisie qu'il faut contrôler
 * pour placer les messages sous les bons champs.
 *
 * Toutes les règles sont réunies dans un seul superRefine, sur un objet dont
 * les champs ne sont que typés : Zod n'exécute pas une règle d'objet quand un
 * champ a déjà échoué, et les messages arriveraient alors en plusieurs tours.
 * Ici, tout ce qui manque est signalé d'un coup.
 */

const INTERVALLES = [
  'interval_years',
  'interval_months',
  'interval_weeks',
  'interval_days',
  'interval_hours',
  'interval_minutes',
  'interval_seconds',
];

const estVide = (valeur) =>
  valeur === null ||
  valeur === undefined ||
  (typeof valeur === 'string' && valeur.trim() === '');

/** Vide, ou entier positif ou nul : ce que parseInt(…) || 0 attend ensuite. */
const estIntervalleValide = (valeur) =>
  estVide(valeur) || (Number.isInteger(Number(valeur)) && Number(valeur) >= 0);

const argument = z.looseObject({
  obligatoire: z.boolean().nullish(),
  valeur: z.any(),
});

export const schemaTache = z
  .looseObject({
    nom: z.string().nullish(),
    fonction: z.any(),
    mode: z.string().nullish(),
    frequence: z.string().nullish(),
    frequence_date: z.string().nullish(),
    end_date: z.string().nullish(),
    days_of_week: z.string().nullish(),
    arguments: z.array(argument).nullish(),
    ...Object.fromEntries(INTERVALLES.map((cle) => [cle, z.any()])),
  })
  .superRefine((tache, ctx) => {
    const signaler = (champ, message) =>
      ctx.addIssue({ code: 'custom', path: [champ], message });

    if (estVide(tache.nom)) {
      signaler('nom', 'saisir_un_nom_de_tache');
    }

    // Sans fonction, handleSave envoyait function_id undefined : la tâche
    // n'aurait rien à exécuter.
    if (estVide(tache.fonction)) {
      signaler('fonction', 'selectionner_une_fonction');
    }

    // Les arguments obligatoires sont marqués d'un astérisque sans que rien ne
    // l'impose ; ils partaient vides.
    (tache.arguments || []).forEach((arg, index) => {
      if (arg.obligatoire && estVide(arg.valeur)) {
        signaler(`argument_${index}`, 'saisir_une_valeur');
      }
    });

    if (tache.mode !== 'scheduled') {
      return;
    }

    // Tâche planifiée : les messages se posent sous le bloc de fréquence.
    if (estVide(tache.frequence)) {
      signaler('frequence', 'selectionner_une_frequence');
      return;
    }

    if (tache.frequence === 'days_of_week' && estVide(tache.days_of_week)) {
      signaler('frequence', 'selectionner_un_jour');
    }

    if (tache.frequence === 'custom') {
      const invalides = INTERVALLES.filter(
        (cle) => !estIntervalleValide(tache[cle])
      );
      if (invalides.length > 0) {
        signaler('frequence', 'saisir_uniquement_des_chiffres');
      } else if (INTERVALLES.every((cle) => !(Number(tache[cle]) > 0))) {
        // Tout à zéro donne des expressions cron entièrement en « * », la même
        // chose qu'aucune fréquence : l'intervalle personnalisé n'en est pas un.
        signaler('frequence', 'saisir_un_intervalle');
      }
    }

    // Dates au format AAAA-MM-JJ des champs date : l'ordre alphabétique est
    // l'ordre chronologique.
    if (
      !estVide(tache.frequence_date) &&
      !estVide(tache.end_date) &&
      tache.end_date < tache.frequence_date
    ) {
      signaler('end_date', 'date_de_fin_avant_date_de_debut');
    }
  });
